// ============================================
// Supporter-update pairing (SELF_UPDATE.md Phase 2)
// ============================================
// Desktop + Android app UI: links this install to a Patreon/SubscribeStar
// account so the entitlement-gated (patreon) update channel can auto-update.
// Tauri desktop: the token pair is handed to the shell
// (localhost:1430/__pno_entitlement), which writes it to disk where the Rust
// updater reads it. Android (Capacitor): the refresh handle is stored in
// localStorage (PnO_Entitlement, app-private storage) and read by
// js/ui/android-update.js. The web/itch build is neither, so this stays
// hidden there.

import { L } from '../systems/localization.js';

const API = 'https://www.pinkedandowned.com';
const LOCAL = 'http://localhost:1430';

// L.get returns the key itself when a locale string is missing; fall back to
// the English default so the UI never shows a raw key.
function t(key, en) {
    try { const v = L.get(key); return (v && v !== key) ? v : en; }
    catch (e) { return en; }
}

function isDesktopApp() {
    try { return !!(window.electronAPI && window.electronAPI.shell === 'tauri'); }
    catch (e) { return false; }
}

function isAndroidApp() {
    try { return !!(window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() === 'android'); }
    catch (e) { return false; }
}

function installId() {
    let id = null;
    try { id = localStorage.getItem('PnO_InstallId'); } catch (e) {}
    if (!id) {
        id = 'ins-' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
        try { localStorage.setItem('PnO_InstallId', id); } catch (e) {}
    }
    return id;
}

async function isPaired() {
    if (isAndroidApp()) {
        try { return !!((JSON.parse(localStorage.getItem('PnO_Entitlement') || 'null') || {}).refresh_handle); }
        catch (e) { return false; }
    }
    try {
        const r = await fetch(`${LOCAL}/__pno_entitlement`, { cache: 'no-store' });
        const j = await r.json();
        return !!j.paired;
    } catch (e) { return false; }
}

async function doPair(code) {
    const r = await fetch(`${API}/api/entitlement/pair`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim().toLowerCase(), install_id: installId(),
            platform: isAndroidApp() ? 'android' : 'electron' }),
    });
    if (!r.ok) {
        let msg = t('entitlement.pair_failed', 'Pairing failed') + ' (' + r.status + ')';
        try { msg = (await r.json()).detail || msg; } catch (e) {}
        throw new Error(msg);
    }
    const tok = await r.json();
    if (isAndroidApp()) {
        // No shell server on Android, the handle lives in localStorage
        // (app-private storage, same protection class as the app's files).
        try {
            localStorage.setItem('PnO_Entitlement', JSON.stringify({
                refresh_handle: tok.refresh_handle, provider: tok.provider, tier_cents: tok.tier_cents,
            }));
        } catch (e) { throw new Error(t('entitlement.save_failed', 'Could not save on this device')); }
    } else {
        const w = await fetch(`${LOCAL}/__pno_entitlement`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                refresh_handle: tok.refresh_handle, access_token: tok.access_token,
                provider: tok.provider, tier_cents: tok.tier_cents,
            }),
        });
        if (!w.ok) throw new Error(t('entitlement.save_failed', 'Could not save on this device'));
    }
    // Nudge the in-game update banner to poll right away (the shell runs a fresh
    // supporter-channel check on pairing).
    try { window.dispatchEvent(new Event('pno-paired')); } catch (e) {}
    return tok;
}

async function doUnpair() {
    if (isAndroidApp()) {
        try { localStorage.removeItem('PnO_Entitlement'); } catch (e) {}
        return;
    }
    try { await fetch(`${LOCAL}/__pno_entitlement`, { method: 'DELETE' }); } catch (e) {}
}

function el(tag, attrs, ...kids) {
    const n = document.createElement(tag);
    for (const k in (attrs || {})) {
        if (k === 'class') n.className = attrs[k];
        else if (k === 'text') n.textContent = attrs[k];
        else n.setAttribute(k, attrs[k]);
    }
    kids.forEach(c => n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c));
    return n;
}

async function refresh(section) {
    section.innerHTML = '';
    section.appendChild(el('h3', { class: 'settings-header', text: t('entitlement.header', 'Supporter Updates') }));
    const status = el('p', { class: 'settings-note' });
    section.appendChild(status);

    const paired = await isPaired();
    if (paired) {
        status.textContent = t('entitlement.connected', 'Connected, this install gets Patreon updates automatically.');
        status.style.color = '#4ade80';
        const btn = el('button', { class: 'btn-menu-secondary', text: t('entitlement.disconnect', 'Disconnect') });
        btn.onclick = async () => { await doUnpair(); refresh(section); };
        section.appendChild(btn);
        return;
    }

    status.textContent = t('entitlement.intro', 'Connect your Patreon or SubscribeStar account so this install auto-updates on the supporter channel. Free updates work without connecting.');
    status.style.color = '#9a92ac';

    const links = el('div', { class: 'settings-row' });
    const mkConnect = (href, label) => {
        const a = el('a', { class: 'btn-menu-secondary', href, target: '_blank', rel: 'noopener' }, label);
        if (isAndroidApp()) {
            // Capacitor opens external top-frame navigations in the system
            // browser; target=_blank does nothing in the WebView.
            a.onclick = (e) => { e.preventDefault(); try { window.location.href = href; } catch (err) {} };
        }
        return a;
    };
    links.appendChild(mkConnect(`${API}/api/patreon/login?flow=app`, t('entitlement.connect_patreon', 'Connect Patreon')));
    links.appendChild(mkConnect(`${API}/api/ss/login?flow=app`, t('entitlement.connect_ss', 'Connect SubscribeStar')));
    section.appendChild(links);

    const row = el('div', { class: 'settings-row' });
    const input = el('input', {
        type: 'text', id: 'entitlement-code',
        placeholder: t('entitlement.code_placeholder', 'paste code (e.g. x7k2-9fq4)'),
        autocomplete: 'off', spellcheck: 'false',
    });
    input.style.cssText = 'flex:1;padding:8px 10px;border-radius:8px;border:1px solid #5b4b7a;background:#160f24;color:#fff;font-family:monospace';
    const pairBtn = el('button', { class: 'btn-menu-secondary', text: t('entitlement.pair', 'Pair') });
    const msg = el('p', { class: 'settings-note' });
    pairBtn.onclick = async () => {
        const code = input.value.trim();
        if (!code) { return; }
        pairBtn.disabled = true; msg.textContent = t('entitlement.pairing', 'Pairing...'); msg.style.color = '#9a92ac';
        try {
            await doPair(code);
            refresh(section);
        } catch (e) {
            msg.textContent = 'X ' + (e && e.message ? e.message : t('entitlement.pair_failed', 'Pairing failed'));
            msg.style.color = '#f87171';
            pairBtn.disabled = false;
        }
    };
    row.appendChild(input); row.appendChild(pairBtn);
    section.appendChild(row);
    section.appendChild(msg);
}

// Reused by the first-launch connect nudge (desktop-update.js) so the player
// can finish pairing inline instead of navigating to Settings.
export async function pairWithCode(code) {
    return doPair(code);
}

export function initEntitlementUI() {
    if (!isDesktopApp() && !isAndroidApp()) return;
    const host = document.getElementById('menu-settings');
    if (!host || document.getElementById('entitlement-section')) return;
    const section = el('div', { id: 'entitlement-section', class: 'entitlement-section' });
    section.style.cssText = 'margin-top:16px;padding-top:12px;border-top:1px solid rgba(255,255,255,0.08)';
    host.appendChild(section);
    refresh(section);
}
